import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import './App.css';
import SignIn from './pages/SignIn';
import Dashboard from './pages/Dashboard';
import ProtectedRoute from './components/ProtectedRoute';
import Services from './pages/Services';
import Products from './pages/Products';
import Categories from './pages/Categories';
import Brands from './pages/Brands';
import Units from './pages/Units';
import Users from './pages/Users';
import Customers from './pages/Customers';
import Staff from './pages/Staff';
import Sales from './pages/Sales';
import SalesView from './pages/SalesView';
import SalesTransactionsLayout from './pages/sales-transactions/SalesTransactionsLayout';
import SalesInvoiceOverlay from './pages/sales-transactions/SalesInvoiceOverlay';
import SalesInvoicePreview from './pages/sales-transactions/SalesInvoicePreview';
import SalesPaymentPreview from './pages/sales-transactions/SalesPaymentPreview';
import SalesPaymentReceiveOverlay from './pages/sales-transactions/SalesPaymentReceiveOverlay';
import SalesPaymentAllocationsOverlay from './pages/sales-transactions/SalesPaymentAllocationsOverlay';
import SalesQuotationPreview from './pages/sales-transactions/SalesQuotationPreview';
import SalesQuotationOverlay from './pages/sales-transactions/SalesQuotationOverlay';
import ReceivePaymentsLayout from './pages/ReceivePaymentsLayout';
import ReceivePaymentsInvoiceOverlay from './pages/ReceivePaymentsInvoiceOverlay';
import ReceivePaymentsPaymentAllocationsOverlay from './pages/ReceivePaymentsPaymentAllocationsOverlay';
import PayBillsLayout from './pages/PayBillsLayout';
import PayBillsBillOverlay from './pages/PayBillsBillOverlay';
import ReceivePaymentUpdate from './pages/payments/ReceivePaymentUpdate';
import PayBillPaymentUpdate from './pages/payments/PayBillPaymentUpdate';
import Inventory from './pages/Inventory';
import InventoryTransactionsLayout from './pages/inventory-transactions/InventoryTransactionsLayout';
import InventoryBillPreview from './pages/inventory-transactions/InventoryBillPreview';
import InventoryInvoicePreview from './pages/inventory-transactions/InventoryInvoicePreview';
import InventoryAdjustmentPreview from './pages/inventory-transactions/InventoryAdjustmentPreview';
import InventoryStartingValuePreview from './pages/inventory-transactions/InventoryStartingValuePreview';
import InventoryInvoiceEditOverlay from './pages/inventory-transactions/InventoryInvoiceEditOverlay';
import InventoryPaymentReceiveOverlay from './pages/inventory-transactions/InventoryPaymentReceiveOverlay';
import InventoryBillEditOverlay from './pages/inventory-transactions/InventoryBillEditOverlay';
import InventoryPaymentPayOverlay from './pages/inventory-transactions/InventoryPaymentPayOverlay';
import InventoryAdjustments from './pages/InventoryAdjustments';
import InventoryAdjustmentManage from './pages/InventoryAdjustmentManage';
import InventoryAdjustmentView from './pages/InventoryAdjustmentView';
import InventoryStartingValueManage from './pages/InventoryStartingValueManage';
import InventoryStartingValueBulkImport from './pages/InventoryStartingValueBulkImport';
import InventoryStartingValueView from './pages/InventoryStartingValueView';
import PhysicalStockManage from './pages/PhysicalStockManage';
import Purchases from './pages/Purchases';
import PurchaseView from './pages/PurchaseView';
import PurchaseTransactionsLayout from './pages/purchase-transactions/PurchaseTransactionsLayout';
import PurchaseBillOverlay from './pages/purchase-transactions/PurchaseBillOverlay';
import PurchaseBillPreview from './pages/purchase-transactions/PurchaseBillPreview';
import PurchasePaymentPreview from './pages/purchase-transactions/PurchasePaymentPreview';
import PurchasePaymentPayOverlay from './pages/purchase-transactions/PurchasePaymentPayOverlay';
import PurchasePaymentAllocationsOverlay from './pages/purchase-transactions/PurchasePaymentAllocationsOverlay';
import Suppliers from './pages/Suppliers';
import CustomersManage from './pages/CustomersManage';
import SalesManage from './pages/SalesManage';
import Quotations from './pages/sales/Quotations';
import QuotationsManage from './pages/sales/QuotationsManage';
import UsersManage from './pages/UsersManage';
import ProductsManage from './pages/ProductsManage';
import CategoriesManage from './pages/CategoriesManage';
import BrandsManage from './pages/BrandsManage';
import UnitsManage from './pages/UnitsManage';
import StaffManage from './pages/StaffManage';
import SuppliersManage from './pages/SuppliersManage';
import PurchasesManage from './pages/PurchasesManage';
import PurchaseOrders from './pages/purchase/PurchaseOrders';
import PurchaseOrdersManage from './pages/purchase/PurchaseOrdersManage';
import Reports from './pages/reports/Reports';
import CustomerBalanceSummary from './pages/reports/CustomerBalanceSummary';
import VendorBalanceSummary from './pages/reports/VendorBalanceSummary';
import SalesByCustomerSummary from './pages/reports/SalesByCustomerSummary';
import SalesByProductSummary from './pages/reports/SalesByProductSummary';
import IncomeByCustomerSummary from './pages/reports/IncomeByCustomerSummary';
import BankCollateralStock from './pages/reports/BankCollateralStock';
import Accounts from './pages/Accounts';
import Settings from './pages/Settings';
import POS from './pages/POS';
import { Toaster } from 'sonner';
import { isAuthenticated } from './utils/auth';
import AuthProvider from './components/AuthProvider';
import PlatformLogin from './pages/platform-admin/Login';
import PlatformDashboard from './pages/platform-admin/Dashboard';
import PlatformProtectedRoute from './components/platform-admin/PlatformProtectedRoute';
import PlatformAdminLayout from './components/platform-admin/PlatformAdminLayout';
import { isPlatformAuthenticated } from './utils/platformAuth';

function App() {
  return (
    <Router>
      <AuthProvider>
        <Toaster position="top-center" richColors closeButton duration={2000} />
        <Routes>
          {/* Root route - redirect based on auth status */}
          <Route
            path="/"
            element={
              isAuthenticated() ? <Navigate to="/dashboard" replace /> : <Navigate to="/signin" replace />
            }
          />

          {/* Auth routes */}
          <Route path="/signin" element={<SignIn />} />

          {/* Platform admin routes */}
          <Route path="/platform-admin" element={<PlatformAdminLayout />}>
            <Route
              index
              element={
                isPlatformAuthenticated() ? (
                  <Navigate to="/platform-admin/dashboard" replace />
                ) : (
                  <Navigate to="/platform-admin/login" replace />
                )
              }
            />
            <Route path="login" element={<PlatformLogin />} />
            <Route
              path="dashboard"
              element={
                <PlatformProtectedRoute>
                  <PlatformDashboard />
                </PlatformProtectedRoute>
              }
            />
          </Route>

          {/* Protected Dashboard routes */}
          <Route
            path="/dashboard"
            element={
              <ProtectedRoute>
                <Dashboard />
              </ProtectedRoute>
            }
          />
          <Route
            path="/users"
            element={
              <ProtectedRoute>
                <Users />
              </ProtectedRoute>
            }
          />
          <Route
            path="/users/manage"
            element={
              <ProtectedRoute>
                <UsersManage />
              </ProtectedRoute>
            }
          />

          <Route
            path="/services"
            element={
              <ProtectedRoute>
                <Services />
              </ProtectedRoute>
            }
          />
          <Route
            path="/products"
            element={
              <ProtectedRoute>
                <Products />
              </ProtectedRoute>
            }
          />
          <Route
            path="/manage-products"
            element={
              <ProtectedRoute>
                <ProductsManage />
              </ProtectedRoute>
            }
          />

          <Route
            path="/manage-categories"
            element={
              <ProtectedRoute>
                <CategoriesManage />
              </ProtectedRoute>
            }
          />

          <Route
            path="/manage-brands"
            element={
              <ProtectedRoute>
                <BrandsManage />
              </ProtectedRoute>
            }
          />

          <Route
            path="/manage-units"
            element={
              <ProtectedRoute>
                <UnitsManage />
              </ProtectedRoute>
            }
          />

          <Route
            path="/staff/manage"
            element={
              <ProtectedRoute>
                <StaffManage />
              </ProtectedRoute>
            }
          />

          <Route
            path="/manage-suppliers"
            element={
              <ProtectedRoute>
                <SuppliersManage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/categories"
            element={
              <ProtectedRoute>
                <Categories />
              </ProtectedRoute>
            }
          />
          <Route
            path="/brands"
            element={
              <ProtectedRoute>
                <Brands />
              </ProtectedRoute>
            }
          />
          <Route
            path="/units"
            element={
              <ProtectedRoute>
                <Units />
              </ProtectedRoute>
            }
          />
          <Route
            path="/customers"
            element={
              <ProtectedRoute>
                <Customers />
              </ProtectedRoute>
            }
          />
          <Route
            path="/manage-customers"
            element={
              <ProtectedRoute>
                <CustomersManage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/staff"
            element={
              <ProtectedRoute>
                <Staff />
              </ProtectedRoute>
            }
          />
          <Route
            path="/sales"
            element={
              <ProtectedRoute>
                <Sales />
              </ProtectedRoute>
            }
          />
          <Route
            path="/sales/:id"
            element={
              <ProtectedRoute>
                <SalesView />
              </ProtectedRoute>
            }
          />
          <Route
            path="/sales-transactions"
            element={
              <ProtectedRoute>
                <SalesTransactionsLayout />
              </ProtectedRoute>
            }
          >
            <Route path="view/invoice/:id" element={<SalesInvoicePreview />} />
            <Route path="view/payment/:id" element={<SalesPaymentPreview />} />
            <Route path="view/quotation/:id" element={<SalesQuotationPreview />} />
            <Route path="payment/receive" element={<SalesPaymentReceiveOverlay />}>
              <Route path="invoice/:id" element={<ReceivePaymentsInvoiceOverlay />} />
            </Route>
            <Route path="payment/:id/allocations" element={<SalesPaymentAllocationsOverlay />} />
            <Route path="invoice/:id" element={<SalesInvoiceOverlay />} />
            <Route path="quotation/:id" element={<SalesQuotationOverlay />} />
          </Route>
          <Route
            path="/receive-payments"
            element={
              <ProtectedRoute>
                <ReceivePaymentsLayout />
              </ProtectedRoute>
            }
          >
            <Route path="invoice/:id" element={<ReceivePaymentsInvoiceOverlay />} />
            <Route
              path="payment/:id/allocations"
              element={<ReceivePaymentsPaymentAllocationsOverlay />}
            />
          </Route>
          <Route
            path="/pay-bills"
            element={
              <ProtectedRoute>
                <PayBillsLayout />
              </ProtectedRoute>
            }
          >
            <Route path="bill/:id" element={<PayBillsBillOverlay />} />
          </Route>
          <Route
            path="/customer-payments/:id/edit-allocations"
            element={
              <ProtectedRoute>
                <ReceivePaymentUpdate />
              </ProtectedRoute>
            }
          />
          <Route
            path="/bill-payments/:id/edit-allocations"
            element={
              <ProtectedRoute>
                <PayBillPaymentUpdate />
              </ProtectedRoute>
            }
          />
          <Route
            path="/manage-sales"
            element={
              <ProtectedRoute>
                <SalesManage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/quotations"
            element={
              <ProtectedRoute>
                <Quotations />
              </ProtectedRoute>
            }
          />
          <Route
            path="/quotations/manage"
            element={
              <ProtectedRoute>
                <QuotationsManage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/inventory"
            element={
              <ProtectedRoute>
                <Inventory />
              </ProtectedRoute>
            }
          />
          <Route
            path="/inventory-transactions"
            element={
              <ProtectedRoute>
                <InventoryTransactionsLayout />
              </ProtectedRoute>
            }
          >
            <Route path="view/bill/:id" element={<InventoryBillPreview />} />
            <Route path="view/invoice/:id" element={<InventoryInvoicePreview />} />
            <Route path="view/adjustment/:id" element={<InventoryAdjustmentPreview />} />
            <Route path="view/starting-value/:itemId" element={<InventoryStartingValuePreview />} />
            <Route path="invoice/:id" element={<InventoryInvoiceEditOverlay />} />
            <Route path="payment/receive" element={<InventoryPaymentReceiveOverlay />}>
              <Route path="invoice/:id" element={<ReceivePaymentsInvoiceOverlay />} />
            </Route>
            <Route path="bill/:id" element={<InventoryBillEditOverlay />} />
            <Route path="payment/pay" element={<InventoryPaymentPayOverlay />}>
              <Route path="bill/:id" element={<PayBillsBillOverlay />} />
            </Route>
          </Route>
          <Route
            path="/inventory-adjustments"
            element={
              <ProtectedRoute>
                <InventoryAdjustments />
              </ProtectedRoute>
            }
          />
          <Route
            path="/inventory-adjustments/:id"
            element={
              <ProtectedRoute>
                <InventoryAdjustmentView />
              </ProtectedRoute>
            }
          />
          <Route
            path="/manage-inventory-adjustments"
            element={
              <ProtectedRoute>
                <InventoryAdjustmentManage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/inventory-opening-stock-import"
            element={
              <ProtectedRoute>
                <InventoryStartingValueBulkImport />
              </ProtectedRoute>
            }
          />
          <Route
            path="/manage-inventory-starting-values"
            element={
              <ProtectedRoute>
                <InventoryStartingValueManage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/inventory-starting-values/:itemId"
            element={
              <ProtectedRoute>
                <InventoryStartingValueView />
              </ProtectedRoute>
            }
          />
          <Route
            path="/physical-stock-manage"
            element={
              <ProtectedRoute>
                <PhysicalStockManage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/purchases"
            element={
              <ProtectedRoute>
                <Purchases />
              </ProtectedRoute>
            }
          />
          <Route
            path="/purchases/:id"
            element={
              <ProtectedRoute>
                <PurchaseView />
              </ProtectedRoute>
            }
          />
          <Route
            path="/purchase-transactions"
            element={
              <ProtectedRoute>
                <PurchaseTransactionsLayout />
              </ProtectedRoute>
            }
          >
            <Route path="view/bill/:id" element={<PurchaseBillPreview />} />
            <Route path="view/payment/:id" element={<PurchasePaymentPreview />} />
            <Route path="payment/pay" element={<PurchasePaymentPayOverlay />}>
              <Route path="bill/:id" element={<PayBillsBillOverlay />} />
            </Route>
            <Route path="payment/:id/allocations" element={<PurchasePaymentAllocationsOverlay />} />
            <Route path="bill/:id" element={<PurchaseBillOverlay />} />
          </Route>
          <Route
            path="/manage-purchases"
            element={
              <ProtectedRoute>
                <PurchasesManage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/purchase-orders"
            element={
              <ProtectedRoute>
                <PurchaseOrders />
              </ProtectedRoute>
            }
          />
          <Route
            path="/purchase-orders/manage"
            element={
              <ProtectedRoute>
                <PurchaseOrdersManage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/suppliers"
            element={
              <ProtectedRoute>
                <Suppliers />
              </ProtectedRoute>
            }
          />
          <Route
            path="/pos"
            element={
              <ProtectedRoute>
                <POS />
              </ProtectedRoute>
            }
          />
          <Route
            path="/reports"
            element={
              <ProtectedRoute>
                <Reports />
              </ProtectedRoute>
            }
          />
          <Route
            path="/reports/customer-balance-summary"
            element={
              <ProtectedRoute>
                <CustomerBalanceSummary />
              </ProtectedRoute>
            }
          />
          <Route
            path="/reports/vendor-balance-summary"
            element={
              <ProtectedRoute>
                <VendorBalanceSummary />
              </ProtectedRoute>
            }
          />
          <Route
            path="/reports/sales-by-customer-summary"
            element={
              <ProtectedRoute>
                <SalesByCustomerSummary />
              </ProtectedRoute>
            }
          />
          <Route
            path="/reports/sales-by-product-summary"
            element={
              <ProtectedRoute>
                <SalesByProductSummary />
              </ProtectedRoute>
            }
          />
          <Route
            path="/reports/income-by-customer-summary"
            element={
              <ProtectedRoute>
                <IncomeByCustomerSummary />
              </ProtectedRoute>
            }
          />
          <Route
            path="/reports/bank-collateral-stock"
            element={
              <ProtectedRoute>
                <BankCollateralStock />
              </ProtectedRoute>
            }
          />
          <Route
            path="/accounts"
            element={
              <ProtectedRoute>
                <Accounts />
              </ProtectedRoute>
            }
          />
          <Route
            path="/settings"
            element={
              <ProtectedRoute>
                <Settings />
              </ProtectedRoute>
            }
          />

          {/* Add more protected routes as needed */}
          {/* 
          <Route 
            path="/profile" 
            element={
              <ProtectedRoute>
                <Profile />
              </ProtectedRoute>
            } 
          />
          */}

          {/* Catch all - redirect to signin */}
          <Route path="*" element={<Navigate to="/signin" replace />} />
        </Routes>
      </AuthProvider>
    </Router>
  );
}

export default App;